import { defineContract, listOf, nullableString, timestamp, z } from "./_base";

/*
 * Connected Google/LinkedIn accounts — the DATA connection, which is a
 * different thing from Google SSO sign-in and worth keeping straight:
 *
 *   /auth/google       — proves who you are. Grants a kontact.id session.
 *   /providers/google  — grants kontact.id access to your Contacts, Calendar
 *                        and Gmail so it can sync them.
 *
 * Signing in with Google does NOT connect your data; the scopes are different
 * ("openid email profile" vs the Contacts/Calendar/Gmail scopes) and Google
 * issues them in separate consent flows.
 *
 * Note the redirect for this flow is owned by the SERVER
 * (GOOGLE_OAUTH_REDIRECT_URI -> /api/providers/google/callback), so unlike SSO
 * it does not depend on which port the dashboard runs on.
 */

export const providerAccountSchema = z.object({
  id: z.string(),
  provider: z.string(),
  providerUserId: nullableString,
  isActive: z.boolean().nullish(),
  lastSyncedAt: timestamp,
  scopes: z.union([z.array(z.string()), z.string(), z.null()]).optional(),
  createdAt: timestamp,
});
export type ProviderAccount = z.infer<typeof providerAccountSchema>;

export const providersContract = defineContract({
  name: "providers.list",
  method: "GET",
  path: () => "/providers",
  response: listOf(providerAccountSchema),
});

/** Returns the Google consent URL to send the browser to. The server builds it
 *  with CSRF state + PKCE, so the URL must be used as-is, not reconstructed. */
export const googleConnectContract = defineContract({
  name: "providers.google.connect",
  method: "GET",
  path: () => "/providers/google/connect",
  response: z.object({ url: z.string() }),
  /* returnTo: the API encodes this origin into the OAuth state so the registered callback page
     (the classic web app) bounces the code back here — see ProviderCallback. Allow-listed server-side. */
  params: (input: { scopes?: "all" | "contacts" | "calendar" | "gmail" }) => ({ ...input, returnTo: window.location.origin }),
});

export const providerSyncContract = defineContract({
  name: "providers.sync",
  method: "POST",
  path: (input: { provider: string }) => `/providers/${input.provider}/sync`,
  response: z.unknown(),
});

export const providerDisconnectContract = defineContract({
  name: "providers.disconnect",
  method: "DELETE",
  path: (input: { id: string }) => `/providers/${input.id}`,
  response: z.unknown(),
});

/*
 * Google returns the browser to the FRONTEND at
 * /settings/providers/google/callback (that is the URI registered in the Cloud
 * console), and the page then posts the code here for exchange. The server
 * needs the `state` back as well: it carries the CSRF token and the PKCE
 * verifier lookup, and the exchange is rejected without it.
 */
export const googleCallbackContract = defineContract({
  name: "providers.google.callback",
  method: "POST",
  path: () => "/providers/google/callback",
  response: z.unknown(),
  params: (input: { code: string; state?: string }) => input,
});
